import { MessageCircle, ArrowUp, Instagram, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import { BRAND, DOMAIN, TRANSPARENCY, WA_PRIMARY_LABEL, WA_SECONDARY_LABEL, DIRECT_MSG, waLink } from "@/data/site";
import { RESORTS } from "@/data/resorts";
import { useSiteSettings, formatPhoneDisplay } from "@/lib/settings";
import { scrollToTop } from "@/lib/scroll";

export default function Footer() {
    const settings = useSiteSettings();
    const instagramUrl = settings.instagram_url;

    return (
        <footer className="bg-forest-deep text-stone-300" data-testid="portal-footer">
            <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
                <div className="grid grid-cols-1 gap-12 md:grid-cols-3">
                    <div>
                        <Logo />
                        <p className="mt-5 max-w-xs text-sm leading-relaxed text-stone-400">
                            Curadoria independente de resorts para férias em família, escapadas românticas e eventos —
                            com cotação personalizada direto no seu WhatsApp.
                        </p>
                        <p className="mt-4 text-xs font-medium tracking-wide text-gold">{DOMAIN}</p>
                    </div>

                    <div>
                        <h3 className="text-xs font-semibold uppercase tracking-[0.22em] text-gold">Explore os Resorts</h3>
                        <nav className="mt-5 flex flex-col gap-2.5" aria-label="Resorts">
                            {RESORTS.map((r) => (
                                <Link
                                    key={r.slug}
                                    to={`/resorts/${r.slug}`}
                                    data-testid={`footer-resort-link-${r.slug}`}
                                    className="w-fit text-sm text-stone-400 transition-colors hover:text-gold"
                                >
                                    {r.name} <span className="text-stone-600">· {r.short}</span>
                                </Link>
                            ))}
                        </nav>
                    </div>

                    <div>
                        <h3 className="text-xs font-semibold uppercase tracking-[0.22em] text-gold">Fale com a gente</h3>
                        <div className="mt-5 space-y-3">
                            <a
                                data-testid="footer-whatsapp-primary"
                                href={waLink(settings.whatsapp_primary, DIRECT_MSG)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-3 text-sm transition-colors hover:text-gold"
                            >
                                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-wa/15 text-wa">
                                    <MessageCircle className="h-4 w-4" />
                                </span>
                                <span>
                                    {formatPhoneDisplay(settings.whatsapp_primary)}
                                    <span className="block text-[11px] text-stone-500">{WA_PRIMARY_LABEL}</span>
                                </span>
                            </a>
                            <a
                                data-testid="footer-whatsapp-secondary"
                                href={waLink(settings.whatsapp_secondary, DIRECT_MSG)}
                                target="_blank" rel="noopener noreferrer"
                                className="flex items-center gap-3 text-sm transition-colors hover:text-gold"
                            >
                                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-wa/15 text-wa">
                                    <MessageCircle className="h-4 w-4" />
                                </span>
                                <span>
                                    {formatPhoneDisplay(settings.whatsapp_secondary)}
                                    <span className="block text-[11px] text-stone-500">{WA_SECONDARY_LABEL}</span>
                                </span>
                            </a>
                            {settings.contact_email && (
                                <a
                                    data-testid="footer-email"
                                    href={`mailto:${settings.contact_email}`}
                                    className="flex items-center gap-3 text-sm transition-colors hover:text-gold"
                                >
                                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gold/15 text-gold">
                                        <Mail className="h-4 w-4" />
                                    </span>
                                    <span>
                                        {settings.contact_email}
                                        <span className="block text-[11px] text-stone-500">E-mail corporativo</span>
                                    </span>
                                </a>
                            )}
                            {instagramUrl && (
                                <a
                                    data-testid="footer-instagram"
                                    href={instagramUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-3 text-sm transition-colors hover:text-gold"
                                >
                                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gold/15 text-gold">
                                        <Instagram className="h-4 w-4" />
                                    </span>
                                    <span>
                                        Instagram
                                        <span className="block text-[11px] text-stone-500">Siga o Destino Resorts</span>
                                    </span>
                                </a>
                            )}
                        </div>
                    </div>
                </div>

                <div className="mt-14 flex flex-col items-center justify-between gap-5 border-t border-white/10 pt-7 sm:flex-row">
                    <div className="max-w-2xl">
                        <p
                            className="text-center text-[11px] leading-relaxed text-stone-500 sm:text-left"
                            data-testid="footer-transparency-disclaimer"
                        >
                            © 2026 {BRAND} · {DOMAIN}. {TRANSPARENCY}
                        </p>
                        {settings.cadastur_number && (
                            <div className="mt-4 flex flex-col items-center gap-3 sm:flex-row sm:justify-start" data-testid="footer-cadastur">
                                <img
                                    src="/cadastur.png"
                                    alt="Cadastur — Ministério do Turismo"
                                    className="h-9 w-9 rounded-full"
                                    loading="lazy"
                                />
                                <p className="text-center text-[11px] leading-relaxed text-stone-500 sm:text-left">
                                    Agência de Turismo registrada · Cadastur nº {settings.cadastur_number}
                                    {settings.cadastur_holder && (
                                        <span className="block">
                                            {settings.cadastur_holder}
                                            {settings.cadastur_validity && ` · Certificado válido até ${settings.cadastur_validity}`}
                                        </span>
                                    )}
                                    {settings.cadastur_url && (
                                        <>
                                            {" · "}
                                            <a
                                                href={settings.cadastur_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                data-testid="footer-cadastur-link"
                                                className="text-gold-hover underline decoration-gold/40 underline-offset-2 transition-colors hover:text-gold"
                                            >
                                                Verificar autenticidade
                                            </a>
                                        </>
                                    )}
                                </p>
                                {settings.cadastur_url && (
                                    <a
                                        href={settings.cadastur_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="footer-cadastur-qr"
                                        title="Aponte a câmera para verificar a autenticidade — Cadastur (Ministério do Turismo)"
                                        className="shrink-0 transition-transform hover:scale-105"
                                    >
                                        <img
                                            src="/cadastur-qrcode.png"
                                            alt="QR Code oficial do certificado Cadastur — Ministério do Turismo"
                                            className="h-12 w-12 rounded-md bg-white p-0.5"
                                            loading="lazy"
                                        />
                                    </a>
                                )}
                            </div>
                        )}
                    </div>
                    <button
                        type="button"
                        data-testid="back-to-top-btn"
                        onClick={() => scrollToTop()}
                        aria-label="Voltar ao topo"
                        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/15 text-stone-300 transition-all duration-200 hover:border-gold hover:text-gold active:scale-90"
                    >
                        <ArrowUp className="h-4 w-4" />
                    </button>
                </div>
            </div>
        </footer>
    );
}
